<template>
    <!-- Карточка шаблона. Нажатие по всей площади выбирает шаблон, звезда — отдельная
         кнопка поверх: два действия на одной карточке, и путать их нельзя.
         Кнопка внутри кнопки недопустима, поэтому карточка — div с role="button",
         а не <button>. -->
    <div
        class="template-card"
        :class="{ 'template-card_selected': selected }"
        role="button"
        tabindex="0"
        :aria-pressed="String(selected)"
        @click="$emit('select', preset)"
        @keydown.enter.prevent="$emit('select', preset)"
        @keydown.space.prevent="$emit('select', preset)"
    >
        <div class="template-card__head">
            <span class="template-card__name">{{ preset.preset_name }}</span>

            <!-- Звезда видна всегда, а не по наведению: на телефоне наводить нечем,
                 и спрятанное в :hover действие там не существует вовсе. -->
            <button
                type="button"
                class="template-card__star"
                :class="{ 'template-card__star_on': preset.is_favorite }"
                :title="preset.is_favorite ? 'Убрать из избранного' : 'Добавить в избранное'"
                :disabled="busy"
                @click.stop="$emit('favorite', preset)"
            >
                ★
            </button>
        </div>

        <p class="template-card__model" v-if="preset.name">{{ preset.name }}</p>

        <!-- Что именно подставит шаблон — видно до нажатия. Без этого выбор шаблона
             остаётся угадыванием: два «Ноутбука» отличаются как раз гарантией
             и сценарием владения. -->
        <p class="template-card__facts">{{ facts }}</p>

        <p class="template-card__description" v-if="preset.preset_description">{{ preset.preset_description }}</p>
    </div>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import InventoryItemStatusTitle from '../../../../js/enums/InventoryItemStatusTitle';
import { EQUIPMENT_KIND_OPTIONS, OWNER_TYPE_OPTIONS } from '../../../../js/inventoryTemplate';

/**
 * Один шаблон в списке выбора.
 *
 * Компонент только показывает и сообщает о нажатиях: ни запросов, ни прав он не знает.
 * Избранное меняет родитель — он же перечитывает список, потому что звезда меняет
 * состав группы «Избранные», а не только вид одной карточки.
 */
@Component
class VInventoryTemplateCard extends Vue {
    @Prop({ type: Object, required: true })
    preset;

    @Prop({ type: Boolean, default: false })
    selected;

    /**
     * Идёт запрос по этой карточке — звезда не должна ловить второе нажатие.
     */
    @Prop({ type: Boolean, default: false })
    busy;

    /**
     * Строка «Ноутбук · На складе · гарантия 12 мес. · Склад».
     *
     * Через разделитель, а не списком полей: в карточке размером с ладонь четыре подписи
     * заняли бы больше места, чем сами значения. Пропущенные значения выпадают, а не
     * показываются прочерками — прочерк здесь не сообщает ничего.
     */
    get facts() {
        const kind = EQUIPMENT_KIND_OPTIONS.find((option) => option.value === this.preset.kind);
        const owner = OWNER_TYPE_OPTIONS.find((option) => option.value === this.preset.owner_type);
        const status = InventoryItemStatusTitle[this.preset.default_status];
        const months = Number(this.preset.warranty_months);

        return [
            kind ? kind.label : null,
            status || null,
            months > 0 ? `гарантия ${months} мес.` : null,
            owner ? owner.label : null,
        ]
            .filter(Boolean)
            .join(' · ');
    }
}

export default VInventoryTemplateCard;
</script>
<style lang="scss">
.template-card {
    display: flex;
    height: 100%;
    flex-direction: column;
    gap: 2px;
    padding: 12px 14px;
    border: 1px solid #e3e5e8;
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;

    &:hover,
    &:focus-visible {
        border-color: var(--color-green);
    }

    &_selected {
        border-color: var(--color-green);
        background: #f3fcf8;
        box-shadow: inset 0 0 0 1px var(--color-green);
    }

    &__head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
    }

    &__name {
        font-size: 0.875rem;
        line-height: 1.15rem;
        color: #202124;
        overflow-wrap: anywhere;
    }

    // 32 точки — нижняя граница уверенного попадания пальцем. Отрицательные поля
    // возвращают звезде оптический край карточки, не уменьшая площадь нажатия.
    &__star {
        flex: 0 0 auto;
        width: 32px;
        height: 32px;
        margin: -6px -6px 0 0;
        padding: 0;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: #c4c7ca;
        font-size: 1.125rem;
        line-height: 1;
        cursor: pointer;

        &_on {
            color: #f2b705;
        }

        &:disabled {
            cursor: default;
            opacity: 0.5;
        }
    }

    &__model {
        margin: 0;
        color: #3c4043;
        font-size: 0.8125rem;
        overflow-wrap: anywhere;
    }

    &__facts {
        margin: 2px 0 0;
        color: #5f6368;
        font-size: 0.75rem;
    }

    &__description {
        margin: 4px 0 0;
        color: #9aa0a6;
        font-size: 0.75rem;
        overflow-wrap: anywhere;
    }
}
</style>
